(() => {
/**
 * ProductGallery Custom Element
 * A comprehensive gallery component with Swiper integration, video handling, and accessibility features
 */
class ProductGallery extends HTMLElement {
    constructor() {
        super();
        this.thumbsSwiper = null;
        this.mainSwiper = null;
        this.videoObserver = null;
        this.resizeObserver = null;
        this.visibilityHandler = null;
        this.isGalleryVisible = false;

        // Bind methods to preserve context
        this.updateNavigationVisibility = this.updateNavigationVisibility.bind(this);
        this.handleVideoPlay = this.handleVideoPlay.bind(this);
        this.handleVideoPause = this.handleVideoPause.bind(this);
        this.handleVideoEnded = this.handleVideoEnded.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    /**
     * Check if gallery has only one slide
     */
    get hasSingleSlide() {
        const thumbsContainer = this.querySelector('.gallery-thumbs-swiper');
        const mainContainer = this.querySelector('.gallery-main-swiper');
        return thumbsContainer && mainContainer &&
            thumbsContainer.querySelectorAll('.swiper-slide').length <= 1;
    }

    /**
     * Called when the element is added to the DOM
     */
    connectedCallback() {
        this.initializeGallery();
    }

    /**
     * Called when the element is removed from the DOM
     */
    disconnectedCallback() {
        this.destroy();
    }

    /**
     * Initialize the gallery component
     */
    initializeGallery() {
        if (!this.querySelector('.gallery-thumbs-swiper') || !this.querySelector('.gallery-main-swiper')) {
            console.warn('ProductGallery: Required swiper containers not found');
            return;
        }

        // Don't initialize swipers if there's only one slide
        if (this.hasSingleSlide) {
            console.log('ProductGallery: Single slide detected, skipping swiper initialization');
            this.setupVideoHandling();
            this.setupIntersectionObserver();
            this.setupEventListeners();
            return;
        }

        this.setupThumbsSwiper();
        this.setupMainSwiper();
        this.setupVideoHandling();
        this.setupIntersectionObserver();
        this.setupEventListeners();
    }

    /**
     * Initialize thumbnail swiper
     */
    setupThumbsSwiper() {
        this.thumbsSwiper = new Swiper(this.querySelector('.gallery-thumbs-swiper'), {
            spaceBetween: 10,
            grabCursor: true,
            slidesPerView: 4,
            watchSlidesProgress: true,
            navigation: {
                nextEl: this.querySelector('.slider-button--next'),
                prevEl: this.querySelector('.slider-button--prev'),
            },
            breakpoints: {
                320: {
                    slidesPerView: 2.4,
                    spaceBetween: 10
                },
                480: {
                    slidesPerView: 3,
                    spaceBetween: 10
                },
                768: {
                    slidesPerView: 5,
                    spaceBetween: 10
                }
            },
            on: {
                init: (swiper) => this.updateNavigationVisibility(swiper),
                resize: (swiper) => this.updateNavigationVisibility(swiper),
                observerUpdate: (swiper) => this.updateNavigationVisibility(swiper),
                slideChange: (swiper) => this.updateNavigationVisibility(swiper),
                transitionEnd: (swiper) => this.updateNavigationVisibility(swiper),
                touchEnd: (swiper) => this.updateNavigationVisibility(swiper)
            }
        });
    }

    /**
     * Initialize main swiper with video handling
     */
    setupMainSwiper() {
        this.mainSwiper = new Swiper(this.querySelector('.gallery-main-swiper'), {
            slidesPerView: 1,
            spaceBetween: 0,
            grabCursor: true,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            watchSlidesProgress: true,
            thumbs: {
                swiper: this.thumbsSwiper
            },
            on: {
                init: () => this.handleSlideInit(),
                slideChange: () => this.handleSlideChange()
            }
        });
    }

    /**
     * Handle initial slide setup - autoplay first video if available
     */
    handleSlideInit() {
        // Safety check - only run if swiper is initialized
        if (!this.mainSwiper || this.hasSingleSlide) {
            return;
        }

        const firstSlide = this.mainSwiper.slides[0];
        const firstVideo = firstSlide?.querySelector('video');
        const firstPlayButton = firstSlide?.querySelector('.play-button');

        if (firstVideo) {
            firstVideo.muted = true;
            firstVideo.controls = false;
            if (firstPlayButton) {
                firstPlayButton.style.display = 'flex';
            }
        }
    }

    /**
     * Handle slide change - pause all videos and play current slide's video
     */
    handleSlideChange() {
        // Safety check - only run if swiper is initialized
        if (!this.mainSwiper || this.hasSingleSlide) {
            return;
        }

        // Hide all play buttons and pause all videos immediately
        this.querySelectorAll('.video-container').forEach(container => {
            const video = container.querySelector('video');
            const playButton = container.querySelector('.play-button');
            if (video) {
                video.pause();
                video.controls = false;
            }
            if (playButton) {
                playButton.style.display = 'none';
            }
        });

        // Play the current slide's video if it exists
        const currentSlide = this.mainSwiper.slides[this.mainSwiper.activeIndex];
        const currentVideo = currentSlide?.querySelector('video');
        const currentPlayButton = currentSlide?.querySelector('.play-button');
        if (currentVideo) {
            if (!this.isGalleryVisible) {
                if (currentPlayButton) currentPlayButton.style.display = 'flex';
                return;
            }
            this.loadVideo(currentVideo);
            currentVideo.muted = true;
            currentVideo.controls = true;
            currentVideo.play()
                .then(() => {
                    if (currentPlayButton) currentPlayButton.style.display = 'none';
                })
                .catch(() => {
                    if (currentPlayButton) currentPlayButton.style.display = 'flex';
                });
        }
    }

    /**
     * Attach a deferred video source only when the gallery needs it.
     */
    loadVideo(video) {
        if (!video || video.dataset.loaded === 'true') return;

        if (video.dataset.poster) {
            video.poster = video.dataset.poster;
            video.removeAttribute('data-poster');
        }

        video.querySelectorAll('source[data-src]').forEach(source => {
            source.src = source.dataset.src;
            source.removeAttribute('data-src');
        });

        video.load();
        video.dataset.loaded = 'true';
    }

    /**
     * Update navigation arrow visibility based on swiper state
     */
    updateNavigationVisibility(swiper) {
        if (!swiper) return;

        const navPrev = this.querySelector('.slider-button--prev');
        const navNext = this.querySelector('.slider-button--next');

        if (navPrev && navNext) {
            // Hide arrows if there's only one slide
            if (this.hasSingleSlide) {
                navPrev.classList.remove('visible');
                navNext.classList.remove('visible');
            } else {
                // Show/hide prev arrow based on position
                if (swiper.isBeginning) {
                    navPrev.classList.remove('visible');
                } else {
                    navPrev.classList.add('visible');
                }
                // Show/hide next arrow based on position
                if (swiper.isEnd) {
                    navNext.classList.remove('visible');
                } else {
                    navNext.classList.add('visible');
                }
            }
        }
    }

    /**
     * Setup video container event handlers
     */
    setupVideoHandling() {
        const videoContainers = this.querySelectorAll('.video-container');

        videoContainers.forEach(container => {
            const video = container.querySelector('video');
            const playButton = container.querySelector('.play-button');

            if (video && playButton) {
                // Click on play button
                playButton.addEventListener('click', this.handleVideoPlay);

                // Video pause/end handlers
                video.addEventListener('pause', this.handleVideoPause);
                video.addEventListener('ended', this.handleVideoEnded);

                // Initial state setup
                video.addEventListener('loadedmetadata', () => {
                    if (video.paused && !container.closest('.swiper-slide-active')) {
                        video.controls = false;
                        playButton.style.display = 'flex';
                    }
                });
            }
        });
    }

    /**
     * Handle video play button click
     */
    handleVideoPlay(event) {
        const playButton = event.currentTarget;
        const container = playButton.closest('.video-container');
        const video = container.querySelector('video');

        this.loadVideo(video);
        video.controls = true;
        video.muted = false;
        video.play()
            .then(() => {
                playButton.style.display = 'none';
            })
            .catch(() => {
                playButton.style.display = 'flex';
            });
    }

    /**
     * Handle video pause event
     */
    handleVideoPause(event) {
        const video = event.target;
        const container = video.closest('.video-container');
        const playButton = container.querySelector('.play-button');

        // Only show play button if this is not the active slide
        if (!container.closest('.swiper-slide-active')) {
            video.controls = false;
            playButton.style.display = 'flex';
        }
    }

    /**
     * Handle video ended event
     */
    handleVideoEnded(event) {
        const video = event.target;
        const playButton = video.closest('.video-container').querySelector('.play-button');

        video.controls = false;
        playButton.style.display = 'flex';
    }

    /**
     * Setup intersection observer for video visibility handling
     */
    setupIntersectionObserver() {
        this.videoObserver = new IntersectionObserver((entries) => {
            const entry = entries[0];
            this.isGalleryVisible = Boolean(entry?.isIntersecting);

            if (!this.isGalleryVisible) {
                this.querySelectorAll('.video-container').forEach(container => {
                    const video = container.querySelector('video');
                    const playButton = container.querySelector('.play-button');
                    if (video && !video.paused) video.pause();
                    if (video) video.controls = false;
                    if (playButton) playButton.style.display = 'flex';
                });
                return;
            }

            const activeSlide = this.querySelector('.swiper-slide-active') || this.querySelector('.swiper-slide');
            const video = activeSlide?.querySelector('video');
            const playButton = activeSlide?.querySelector('.play-button');
            if (!video) return;

            this.loadVideo(video);
            video.muted = true;
            video.controls = true;
            video.play()
                .then(() => {
                    if (playButton) playButton.style.display = 'none';
                })
                .catch(() => {
                    if (playButton) playButton.style.display = 'flex';
                });
        }, {
            root: null,
            rootMargin: '300px 0px',
            threshold: 0.01
        });

        this.videoObserver.observe(this);
    }

    /**
     * Setup global event listeners
     */
    setupEventListeners() {
        // Handle window resize (only if swipers are initialized)
        if (!this.hasSingleSlide) {
            this.resizeObserver = new ResizeObserver(() => {
                if (this.thumbsSwiper) {
                    this.updateNavigationVisibility(this.thumbsSwiper);
                }
            });
        }

        // Handle window visibility changes
        this.visibilityHandler = this.handleVisibilityChange;
        document.addEventListener('visibilitychange', this.visibilityHandler);

        // Update navigation visibility after everything is loaded
        window.addEventListener('load', () => {
            if (this.thumbsSwiper && !this.hasSingleSlide) {
                this.updateNavigationVisibility(this.thumbsSwiper);
            }
        });
    }

    /**
     * Handle window visibility changes
     */
    handleVisibilityChange() {
        if (document.hidden) {
            // Page is hidden, pause all videos
            this.querySelectorAll('.video-container video').forEach(video => {
                if (!video.paused) {
                    video.pause();
                    video.controls = false;
                    const playButton = video.closest('.video-container').querySelector('.play-button');
                    if (playButton) {
                        playButton.style.display = 'flex';
                    }
                }
            });
        } else {
            // Page becomes visible again - play active video muted
            const activeSlide = this.querySelector('.swiper-slide-active');
            const activeVideo = activeSlide?.querySelector('video');
            const activePlayButton = activeSlide?.querySelector('.play-button');

            if (activeVideo && this.isGalleryVisible) {
                this.loadVideo(activeVideo);
                activeVideo.muted = true;
                activeVideo.controls = true;
                activeVideo.play()
                    .then(() => {
                        if (activePlayButton) activePlayButton.style.display = 'none';
                    })
                    .catch(() => {
                        if (activePlayButton) activePlayButton.style.display = 'flex';
                    });
            }
        }
    }

    /**
     * Destroy the gallery and clean up event listeners
     */
    destroy() {
        // Destroy swipers (only if they were initialized)
        if (this.thumbsSwiper && !this.hasSingleSlide) {
            this.thumbsSwiper.destroy(true, true);
            this.thumbsSwiper = null;
        }

        if (this.mainSwiper && !this.hasSingleSlide) {
            this.mainSwiper.destroy(true, true);
            this.mainSwiper = null;
        }

        // Disconnect observers
        if (this.videoObserver) {
            this.videoObserver.disconnect();
            this.videoObserver = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Remove event listeners
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        // Remove video event listeners
        this.querySelectorAll('.video-container').forEach(container => {
            const video = container.querySelector('video');
            const playButton = container.querySelector('.play-button');

            if (video && playButton) {
                playButton.removeEventListener('click', this.handleVideoPlay);
                video.removeEventListener('pause', this.handleVideoPause);
                video.removeEventListener('ended', this.handleVideoEnded);
            }
        });
    }
}

// Register the custom element
if (!customElements.get('product-gallery')) {
    customElements.define('product-gallery', ProductGallery);
}

// // Legacy initialization for existing implementations
// document.addEventListener('DOMContentLoaded', function () {
//     const gallerySection = document.querySelector('.product-gallery-section');
//     if (!gallerySection) return;

//     // Initialize thumbnail swiper
//     const thumbsSwiper = new Swiper('.gallery-thumbs-swiper', {
//         spaceBetween: 10,
//         grabCursor: true,
//         slidesPerView: 4,
//         watchSlidesProgress: true,
//         navigation: {
//             nextEl: '.slider-button--next',
//             prevEl: '.slider-button--prev',
//         },
//         breakpoints: {
//             320: {
//                 slidesPerView: 2.4,
//                 spaceBetween: 10
//             },
//             480: {
//                 slidesPerView: 3,
//                 spaceBetween: 10
//             },
//             768: {
//                 slidesPerView: 5,
//                 spaceBetween: 10
//             }
//         },
//         on: {
//             init: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             },
//             resize: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             },
//             observerUpdate: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             },
//             slideChange: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             },
//             transitionEnd: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             },
//             touchEnd: function (swiper) {
//                 updateNavigationVisibility(swiper);
//             }
//         }
//     });

//     // Helper function to check if gallery section has only one slide
//     function hasSingleSlide(gallerySection) {
//         const thumbsContainer = gallerySection.querySelector('.gallery-thumbs-swiper');
//         const mainContainer = gallerySection.querySelector('.gallery-main-swiper');
//         return thumbsContainer && mainContainer &&
//             thumbsContainer.querySelectorAll('.swiper-slide').length <= 1;
//     }

//     // Function to update navigation arrow visibility
//     function updateNavigationVisibility(swiper) {
//         if (!swiper) return;

//         const navPrev = gallerySection.querySelector('.slider-button--prev');
//         const navNext = gallerySection.querySelector('.slider-button--next');

//         if (navPrev && navNext) {
//             // Hide arrows if there's only one slide
//             if (hasSingleSlide(gallerySection)) {
//                 navPrev.classList.remove('visible');
//                 navNext.classList.remove('visible');
//             } else {
//                 // Show/hide prev arrow based on position
//                 if (swiper.isBeginning) {
//                     navPrev.classList.remove('visible');
//                 } else {
//                     navPrev.classList.add('visible');
//                 }
//                 // Show/hide next arrow based on position
//                 if (swiper.isEnd) {
//                     navNext.classList.remove('visible');
//                 } else {
//                     navNext.classList.add('visible');
//                 }
//             }
//         }
//     }

//     // Initialize main swiper
//     const mainSwiper = new Swiper('.gallery-main-swiper', {
//         slidesPerView: 1,
//         spaceBetween: 0,
//         grabCursor: true,
//         effect: 'fade',
//         fadeEffect: {
//             crossFade: true
//         },
//         watchSlidesProgress: true,
//         thumbs: {
//             swiper: thumbsSwiper
//         },
//         on: {
//             init: function () {
//                 // Autoplay first video if it exists
//                 const firstSlide = this.slides[0];
//                 const firstVideo = firstSlide?.querySelector('video');
//                 const firstPlayButton = firstSlide?.querySelector('.play-button');
//                 if (firstVideo) {
//                     firstVideo.muted = true;
//                     firstVideo.controls = true;
//                     firstVideo.play();
//                     if (firstPlayButton) {
//                         firstPlayButton.style.display = 'none';
//                     }
//                 }
//             },
//             slideChange: function () {
//                 // Hide all play buttons and pause all videos immediately
//                 document.querySelectorAll('.video-container').forEach(container => {
//                     const video = container.querySelector('video');
//                     const playButton = container.querySelector('.play-button');
//                     if (video) {
//                         video.pause();
//                         video.controls = false;
//                     }
//                     if (playButton) {
//                         playButton.style.display = 'none';
//                     }
//                 });

//                 // Play the current slide's video if it exists
//                 const currentSlide = this.slides[this.activeIndex];
//                 const currentVideo = currentSlide?.querySelector('video');
//                 if (currentVideo) {
//                     currentVideo.muted = false;
//                     currentVideo.controls = true;
//                     currentVideo.play();
//                 }
//             }
//         }
//     });

//     // Check navigation on window resize
//     window.addEventListener('resize', function () {
//         if (thumbsSwiper) {
//             updateNavigationVisibility(thumbsSwiper);
//         }
//     });

//     // Update navigation visibility after everything is loaded
//     window.addEventListener('load', function () {
//         if (thumbsSwiper) {
//             updateNavigationVisibility(thumbsSwiper);
//         }
//     });

//     // Handle video play functionality
//     const videoContainers = document.querySelectorAll('.video-container');

//     videoContainers.forEach(container => {
//         const video = container.querySelector('video');
//         const playButton = container.querySelector('.play-button');

//         if (video && playButton) {
//             // Click on play button
//             playButton.addEventListener('click', function () {
//                 video.controls = true;
//                 video.muted = false;
//                 video.play();
//                 playButton.style.display = 'none';
//             });

//             // When video pauses or ends
//             video.addEventListener('pause', function () {
//                 // Only show play button if this is not the active slide
//                 if (!container.closest('.swiper-slide-active')) {
//                     video.controls = false;
//                     playButton.style.display = 'flex';
//                 }
//             });

//             video.addEventListener('ended', function () {
//                 video.controls = false;
//                 playButton.style.display = 'flex';
//             });

//             // Initial state - ensure play button is visible
//             video.addEventListener('loadedmetadata', function () {
//                 if (video.paused && !container.closest('.swiper-slide-active')) {
//                     video.controls = false;
//                     playButton.style.display = 'flex';
//                 }
//             });
//         }
//     });

//     // Setup Intersection Observer to handle video playback based on visibility
//     const videoObserver = new IntersectionObserver((entries) => {
//         entries.forEach(entry => {
//             const videoContainer = entry.target;
//             const video = videoContainer.querySelector('video');
//             const playButton = videoContainer.querySelector('.play-button');

//             if (!entry.isIntersecting) {
//                 // Video is not visible - pause it
//                 if (video && !video.paused) {
//                     video.pause();
//                     video.controls = false;
//                     if (playButton) {
//                         playButton.style.display = 'flex';
//                     }
//                 }
//             } else {
//                 // Video is visible - play it if it's the active slide
//                 if (video && videoContainer.closest('.swiper-slide-active')) {
//                     video.muted = true;
//                     video.controls = true;
//                     video.play();
//                     if (playButton) {
//                         playButton.style.display = 'none';
//                     }
//                 }
//             }
//         });
//     }, {
//         root: null,
//         threshold: 0.3
//     });

//     // Observe all video containers
//     videoContainers.forEach(container => {
//         videoObserver.observe(container);
//     });

//     // Handle window visibility changes
//     document.addEventListener('visibilitychange', function () {
//         if (document.hidden) {
//             // Page is hidden, pause all videos
//             document.querySelectorAll('.video-container video').forEach(video => {
//                 if (!video.paused) {
//                     video.pause();
//                     video.controls = false;
//                     const playButton = video.closest('.video-container').querySelector('.play-button');
//                     if (playButton) {
//                         playButton.style.display = 'flex';
//                     }
//                 }
//             });
//         } else {
//             // Page becomes visible again - play active video muted
//             const activeSlide = document.querySelector('.swiper-slide-active');
//             const activeVideo = activeSlide?.querySelector('video');
//             const activePlayButton = activeSlide?.querySelector('.play-button');

//             if (activeVideo) {
//                 activeVideo.muted = true;
//                 activeVideo.controls = true;
//                 activeVideo.play();
//                 if (activePlayButton) {
//                     activePlayButton.style.display = 'none';
//                 }
//             }
//         }
//     });
// });
})();
